// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  message,
  Row,
  Select,
  Tooltip
} from "antd";
import {
  dateFormat,
  FIELD_LENGTH,
  formItemBlockLayout,
  USER_WORKFLOW_STATUS
} from "../../../../utils/Constant";
import { SearchOutlined } from "@ant-design/icons";
import {
  UserSearchRequestDTO,
  NewsThemeInfoResponseDTO
} from "../../../../api/backend-openapi";
import FormField from "../../../components/FormField";
import { useStore } from "effector-react";
import "moment/locale/fr";
import locale from "antd/es/date-picker/locale/fr_FR";
import { requestGetNewsThemes } from "../../../../state/newsTheme/NewsThemeEffects";
import { setNewsThemes } from "../../../../state/newsTheme/NewsThemeEvents";
import { requestGetUsersList } from "../../../../state/userManagement/UserManagementEffects";
import { NewsThemeStore } from "../../../../state/newsTheme/NewsThemeStore";
import { UsersSearchFormProps } from "../../../types/UserManagementTypes";
import { BranchesStore } from "../../../../state/branches/BranchesStore";

const UsersListSearchForm: React.FunctionComponent<UsersSearchFormProps> = (
  props: UsersSearchFormProps
) => {
  const {
    pagination,
    dataHasLoaded,
    setDataHasLoaded,
    onSearchDtoUpdate
  } = props;
  const { t } = useTranslation();

  const branchList = useStore(BranchesStore);

  const [branch, setBranch] = useState<string | undefined>(undefined);

  const [newsThemeId, setNewsThemeId] = useState<string | undefined>(undefined);

  // call getNewsTheme on page load
  useEffect(() => {
    requestGetNewsThemes();
  }, []);

  useEffect(() => {
    return requestGetNewsThemes.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setNewsThemes(result.data);
        setDataHasLoaded(true);
      } else {
        message.error(t("news_themes.load_failed"));
      }
    });
  });

  const handleSubmit = (values: any) => {
    setDataHasLoaded(false);
    const searchCriteria: UserSearchRequestDTO = {
      sub: values.sub,
      email: values.email,
      branch: values.branch,
      newsThemeId: values.newsThemeId,
      creationDateStart: values.creationRange
        ? values.creationRange[0].format("YYYY-MM-DD")
        : undefined,
      creationDateEnd: values.creationRange
        ? values.creationRange[1].format("YYYY-MM-DD")
        : undefined,
      status: values.status,
      id: values.id
    };
    onSearchDtoUpdate(searchCriteria);
    requestGetUsersList({
      pagination,
      dto: searchCriteria
    });
  };

  const { RangePicker } = DatePicker;

  const newsThemeStore: NewsThemeInfoResponseDTO[] | null = useStore(
    NewsThemeStore
  );

  return (
    <Form
      {...formItemBlockLayout}
      onFinish={handleSubmit}
      fields={[
        {
          name: "newsThemeId",
          value: newsThemeId
        }
      ]}
    >
      <div className="d-flex flex-row justify-content-end align-items-stretch flex-wrap">
        <div className="flex-grow-1 d-flex align-items-center">
          <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
            <h3 className="text-secondary mb-0">
              {t("user_list.search_title")}
            </h3>
          </Divider>
        </div>
        <div className="d-flex align-items-center divider-sm-transparent">
          <Divider orientation="right" className="mt-2 mt-sm-3 mb-3">
            <Button
              type="default"
              className="btn-tertiary"
              htmlType="submit"
              loading={!dataHasLoaded}
            >
              <SearchOutlined /> {t("users.global_buttons.search")}
            </Button>
          </Divider>
        </div>
      </div>
      <Row>
        <Col xs={24} sm={12} lg={6}>
          <Form.Item
            name="branch"
            key="branch"
            label={<span>{t("add_users.branch.select")}</span>}
            rules={[]}
          >
            <Select
              id="branch"
              onChange={(value: string) => {
                setBranch(value);
                if (!!value) {
                  setNewsThemeId(undefined);
                }
              }}
              placeholder={t("add_users.branch.placeholder")}
              allowClear
            >
              {Object.keys(branchList).map(key => (
                <Select.Option key={key} value={key}>
                  {branchList[key].label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="newsThemeId"
            key="newsThemeId"
            label={<span>{t("add_users.theme.select")}</span>}
            rules={[]}
          >
            <Select
              id="newsThemeId"
              onChange={(value: string) => setNewsThemeId(value)}
              placeholder={t("add_users.theme.placeholder")}
              allowClear
            >
              {newsThemeStore
                .filter(
                  (newsTheme: NewsThemeInfoResponseDTO) =>
                    !branch || newsTheme.branch === branch
                )
                .map((newsTheme: NewsThemeInfoResponseDTO) => {
                  const label = branch
                    ? newsTheme.name
                    : `${branchList[newsTheme.branch]?.label} - ${
                        newsTheme.name
                      }`;
                  return (
                    <Select.Option key={newsTheme.id} value={newsTheme.id}>
                      <Tooltip title={label}>
                        <span>{label}</span>
                      </Tooltip>
                    </Select.Option>
                  );
                })}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={14} lg={10}>
          <Form.Item
            name="creationRange"
            key="creationRange"
            label={<span>{t("user_list.search_creation_range")}</span>}
          >
            <RangePicker
              locale={locale}
              format={dateFormat}
              placeholder={[t("user.start_date"), t("user.end_date")]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={10} lg={6}>
          <Form.Item
            name="status"
            key="status"
            label={<span>{t("user.status")}</span>}
            rules={[]}
          >
            <Select id="status" placeholder={t("global.select")} allowClear>
              {Object.keys(USER_WORKFLOW_STATUS).map((status: any) => (
                <Select.Option key={status} value={status}>
                  {USER_WORKFLOW_STATUS[status].label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={6} lg={4}>
          <FormField
            labelName={t("user.id")}
            inputId="id"
            required={false}
            emptyErrorMessage={t("global.empty_error_field")}
            maxLength={FIELD_LENGTH.NAME}
          />
        </Col>
        <Col xs={24} sm={6} lg={4}>
          <FormField
            labelName={t("user.sub")}
            inputId="sub"
            required={false}
            emptyErrorMessage={t("global.empty_error_field")}
            maxLength={FIELD_LENGTH.ENTITY_ID}
          />
        </Col>
        <Col xs={24} sm={12} lg={10}>
          <FormField
            labelName={t("user.email")}
            inputId="email"
            required={false}
            emptyErrorMessage={t("global.empty_error_field")}
            maxLength={FIELD_LENGTH.MAIL}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default UsersListSearchForm;
